import { Typography, Box, Alert, FormHelperText, TextField, IconButton, InputAdornment, CircularProgress } from "@mui/material";
import { CustomErrorLogger } from "../utils/CustomErrorLogger";
import { useNavigate, useParams } from 'react-router-dom';
import PasswordHint from "../utils/passwordHint";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import Iconify from "../components/Iconify";
import { LoadingButton } from "@mui/lab";
import Page from "../components/Page";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from "axios";
import '../css/UserResetPassword.css';

const UserResetPassword = () => {

  const { t } = useTranslation();
  const { accessCodeId } = useParams();
  const navigate = useNavigate();

  const [buttonLoading, setButtonLoading] = useState(false);
  const [pageIsLoading, setPageisLoading] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { status } = await axios.get(`forgot-password-access-code/${accessCodeId}`);
        if (status === 200 || status === 201 || status === 202 || status === 204) {
          setPageisLoading(false);
        }
      } catch (err) {
        setError(err.message, 'error fetch access code data in UserResetPassword');
        console.error('Error Fetching access code data:', err);
        CustomErrorLogger(err, 'error fetch access code data in UserResetPassword');
      } finally {
        setPageisLoading(false);
      }
    };

    fetchData();
  }, [accessCodeId]);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object().shape({
      newPassword: Yup.string()
        .trim()
        .min(12, t('passwordMinLength'))
        .matches(/[a-z]/, t('passwordLowercase'))
        .matches(/[A-Z]/, t('passwordUppercase'))
        .matches(/[0-9]/, t('passwordNumber'))
        .matches(/[!@#$%^&*.]/, t('passwordSpecialChar'))
        .required(t('passwordRequired')),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], t('passwordsMustMatch'))
        .required(t('confirmPasswordRequired')),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setButtonLoading(true);
      setError(null);
      try {
        const { status } = await axios.put(`forgot-password/ChangePassforgotPassword/${accessCodeId}`, { hpassword: values.newPassword });
        if (status === 200 || status === 201 || status === 202 || status === 204) {
          navigate("/login");
        }
      } catch (err) {
        setError(err.message);
        CustomErrorLogger(err, 'error change password in UserResetPassword');
      } finally {
        setButtonLoading(false);
      }
    },
  });

  const { handleSubmit, values, handleChange, handleBlur, touched, errors, isValid } = formik;

  return (
    <Page title="Reset Password" className='RP_container_pass'>

      {pageIsLoading ?
        <CircularProgress color="inherit" size="5rem" /> :

        <Box className='RP_card_pass'>

          <form onSubmit={handleSubmit} className="RP_form">

            <Typography className="RP_dialog_title">{t('creatOwnPass')}</Typography>
            <Typography className="RP_dialog_SubTitle">{t('passwordHint')}</Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <Box width={'100%'}>
              <FormHelperText id="outlined-weight-helper-text" className="RP_input_title">{t('newPassword')}</FormHelperText>
              <TextField
                id="ForgotPassNewPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                fullWidth
                required
                variant="outlined"
                className="customTextField"
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helperText={touched.newPassword && errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword((show) => !show)} edge="end">
                        <Iconify
                          icon={
                            showNewPassword
                              ? "eva:eye-fill"
                              : "eva:eye-off-fill"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box width={'100%'}>
              <FormHelperText id="outlined-weight-helper-text" className="RP_input_title">{t('confirmPassword')}</FormHelperText>
              <TextField
                id="ForgotPassConfirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                required
                variant="outlined"
                className="customTextField"
                color={values.confirmPassword === values.newPassword ? "success" : "error"}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((show) => !show)} edge="end">
                        <Iconify
                          icon={
                            showConfirmPassword
                              ? "eva:eye-fill"
                              : "eva:eye-off-fill"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {values?.newPassword && <PasswordHint password={values.newPassword} />}

            <LoadingButton fullWidth loading={buttonLoading} className='RP_button_valider' type="submit" variant="contained" disabled={!isValid || buttonLoading} >{t("saveNewPassword")}</LoadingButton>

          </form>

        </Box>
      }

    </Page>
  );
}

export default UserResetPassword;